import { MarketClock, SendingPace, type ChangeSending } from "./Sending";
import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "../../components/ui/Icon";
import { hideProvider, shownError } from "../../lib/connectionsApi";
import { drafting, readyApi, stopped, type EmailSend, type ReadyEmail, type ReadyList, type ReadyView } from "../../lib/readyApi";

/**
 * Get ready's cold email part (the prototype's `rdMailHTML`): one list per market, the sending mailbox,
 * the emails as the sender writes them, the postal address every footer carries, and approval. The
 * emails are approved together, exactly at the revisions on screen.
 */

const WORDS = ["no", "one", "two", "three", "four", "five"];
const city = (m: string) => m.split(",")[0].trim();
const message = shownError;
export const building = (l: ReadyList) => ["queued", "running", "waiting", "interrupted"].includes(l.state);

export const listsReady = (lists: ReadyList[]) => lists.length > 0 && lists.every((l) => l.state === "done");
export const listsBuilding = (lists: ReadyList[]) => lists.some(building);

/** A market's Smartlead campaign as a pill. */
function SendPill({ send }: { send: EmailSend }) {
  if (send.campaignState === "on")
    return (
      <span className="pill good">
        <Icon name="check" />
        Sending
      </span>
    );
  if (send.campaignState === "paused") return <span className="pill tight">Paused</span>;
  if (send.campaignState === "ready") return <span className="pill tight">Ready to send</span>;
  return <span className="pill live">{send.campaignState === "unsure" ? "Checking with Smartlead" : "Setting up in Smartlead"}</span>;
}

/** How a market's latest send went, in plain words. */
export function sendLine(send: EmailSend): string | null {
  const s = send.lastSend;
  if (!s) return null;
  const r = s.result;
  if (s.state === "executed") {
    const added = r?.added ?? 0;
    const skipped = r?.skipped?.length ?? 0;
    return `The last send gave Smartlead ${added} ${added === 1 ? "person" : "people"} to email.${skipped ? ` It did not take ${skipped}.` : ""}`;
  }
  if (s.state === "proposed") return "The first send waits for your OK.";
  if (s.state === "failed") return hideProvider(`The last send did not work. ${r?.error ?? ""}`.trim());
  if (s.state === "refused") return "The last send was held back by the goal's rules.";
  if (r?.waiting) return hideProvider(r.waiting);
  if (s.state === "approved" || s.state === "executing") return "Sending now.";
  return null;
}

function ListRow({ l, send, onRetry, busy, clock }: { l: ReadyList; send: EmailSend | undefined; onRetry: () => void; busy: boolean; clock: ReactNode }) {
  let p: string;
  let pill: ReactNode;
  let retry = false;
  if (l.state === "done") {
    p = `${l.withEmail} people who published their own address, each saved with the page and the date.`;
    pill = (
      <span className="pill good">
        <Icon name="check" />
        {l.withEmail} found
      </span>
    );
  } else if (building(l)) {
    p = `Finding people in ${city(l.market)} who published their own address, and saving each with the page and the date.${l.people ? ` ${l.people} so far.` : ""}`;
    pill = <span className="pill live">The agent is building this list</span>;
  } else if (l.state === "not_started") {
    p = "The agent has not started this list yet.";
    pill = <span className="pill tight">Not started</span>;
  } else {
    p = `The list run stopped before it finished.${l.people ? ` It found ${l.people} people.` : ""}`;
    pill = <span className="pill bad">Stopped</span>;
    retry = true;
  }
  const line = send ? sendLine(send) : null;
  return (
    <div className="rd-row">
      <span className="ki">
        <Icon name="users" />
      </span>
      <div>
        <b>{l.market}</b>
        <p>{p}</p>
        {line && <p>{line}</p>}
        {clock}
        {retry && (
          <div className="rd-acts">
            <button type="button" className="btn sm ghost" disabled={busy} onClick={onRetry}>
              Try again
            </button>
          </div>
        )}
      </div>
      {send ? <SendPill send={send} /> : pill}
    </div>
  );
}

/** One line of an email, with each person's own words shown as placeholders. */
export function Line({ text }: { text: string }) {
  if (text.trim() === "{first line}")
    return (
      <p>
        <span className="rd-ph">one true line about them</span>
      </p>
    );
  const parts = text.split(/(\{first name\}|\{first line\})/g);
  return (
    <p>
      {parts.map((x, i) =>
        x === "{first name}" ? (
          <span key={i} className="rd-ph">
            first name
          </span>
        ) : x === "{first line}" ? (
          <span key={i} className="rd-ph">
            one true line about them
          </span>
        ) : (
          x
        ),
      )}
    </p>
  );
}

interface Props {
  goalId: string;
  view: ReadyView;
  /** The day the plan starts cold email, as words. */
  startDay: string | null;
  mailboxReady: boolean;
  who: ReactNode;
  setView: (v: ReadyView) => void;
  reload: () => Promise<void>;
  onDraftAgain: () => void;
  say: (message: string, done?: boolean) => void;
  /** The markets' own time zones the owner set, and how a sending change is saved (a new goal version). */
  timezones: Record<string, string>;
  changeSending: ChangeSending;
}

export function ColdEmail({ goalId, view, startDay, mailboxReady, who, setView, reload, onDraftAgain, say, timezones, changeSending }: Props) {
  const [addr, setAddr] = useState(view.postalAddress ?? "");
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<ReadyEmail[]>([]);
  const [busy, setBusy] = useState(false);
  const saved = view.postalAddress ?? "";
  useEffect(() => setAddr(saved), [saved]);

  const emails = view.emails;
  const approved = emails.length > 0 && emails.every((e) => e.approved);
  // Why: emails already given to Smartlead go out as approved and can no longer change.
  const sending = emails.some((e) => e.launched);
  const ready = listsReady(view.lists);
  const can = ready && addr.trim() !== "" && !editing && emails.length > 0;

  const run = async (call: () => Promise<ReadyView>, ok: string | null) => {
    setBusy(true);
    try {
      setView(await call());
      if (ok) say(ok);
      return true;
    } catch (e) {
      say(message(e), false);
      await reload();
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Why: saved on leaving the field without holding the buttons, so the click that ends the typing still lands.
  const saveAddr = async () => {
    if (addr.trim() === saved) return true;
    try {
      setView(await readyApi.postalAddress(goalId, addr.trim()));
      return true;
    } catch (e) {
      say(message(e), false);
      return false;
    }
  };

  const approve = async () => {
    if (!can || !(await saveAddr())) return;
    await run(() => readyApi.approveEmails(goalId, Object.fromEntries(emails.map((e) => [String(e.step), e.rev]))), "Approved. Nothing is sent before its date.");
  };

  const openEdit = () => {
    setDrafts(emails.map((e) => ({ ...e })));
    setEditing(true);
  };
  const saveEdit = async () => {
    const changed = drafts
      .map((d) => ({ ...d, subject: d.subject.trim(), body: d.body.trim() }))
      .filter((d) => {
        const e = emails.find((x) => x.step === d.step);
        return e && d.subject && d.body && (d.subject !== e.subject || d.body !== e.body);
      });
    if (!changed.length) {
      setEditing(false);
      say("Nothing changed.");
      return;
    }
    setBusy(true);
    try {
      let last: ReadyView | null = null;
      for (const d of changed) last = await readyApi.editEmail(goalId, d);
      if (last) setView(last);
      setEditing(false);
      say("Saved.");
    } catch (e) {
      say(message(e), false);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const markets = view.lists.map((l) => l.market);
  const sub = `${markets.length ? `${city(markets[0])} first` : "No market yet"}${startDay ? `, starts ${startDay}` : ""}`;
  // Why: the footer as it is sent: the postal address under each email, then Smartlead's unsubscribe line.
  const footer = (
    <div className="rd-foot">
      {addr.trim() ? addr.trim() : <span className="rd-ph">your postal address</span>}
      <br />
      <span className="rd-unsub">{view.emailFooter?.unsubscribeText ?? "Unsubscribe"}</span>
    </div>
  );
  const retry = async (market: string) => {
    setBusy(true);
    try {
      await readyApi.retryList(goalId, market);
      say(`Building the ${city(market)} list again.`);
    } catch (e) {
      say(message(e), false);
    } finally {
      await reload();
      setBusy(false);
    }
  };

  return (
    <section className="card rd-sec" id="rd-mail">
      <div className="rd-h">
        <div>
          <h3>Cold email</h3>
          <p className="sub">{sub}</p>
        </div>
        {who}
      </div>
      <div className="rd-rows">
        {view.lists.length ? (
          view.lists.map((l) => {
            const send = view.emailSends.find((x) => x.market === l.market);
            return (
              <ListRow
                key={l.market}
                l={l}
                send={send}
                busy={busy}
                onRetry={() => void retry(l.market)}
                clock={<MarketClock market={l.market} tz={l.sendTimezone} open={!send} timezones={timezones} change={changeSending} />}
              />
            );
          })
        ) : (
          <div className="rd-row">
            <span className="ki">
              <Icon name="users" />
            </span>
            <div>
              <b>The list</b>
              <p>Add a market on the Channels step to start a list.</p>
            </div>
            <span className="pill tight">Waiting for you</span>
          </div>
        )}
        <div className="rd-row">
          <span className="ki">
            <Icon name="mail" />
          </span>
          <div>
            <b>Sending</b>
            <p>{mailboxReady ? "From the sending address connected above." : "Connect the email provider above first."}</p>
            {view.sending && <SendingPace sending={view.sending} open={view.emailSends.length === 0} change={changeSending} />}
          </div>
          {mailboxReady ? (
            <span className="pill good">
              <Icon name="check" />
              Connected
            </span>
          ) : (
            <span className="pill tight">Waiting for you</span>
          )}
        </div>
      </div>
      {emails.length === 0 ? (
        stopped(view.runs.emails) ? (
          <>
            <p className="rd-p rd-stop" style={{ marginTop: 22 }}>
              {hideProvider(`The agent stopped before writing the emails. ${view.runs.emails?.error ?? ""}`.trim())}
            </p>
            <div className="rd-acts">
              <button type="button" className="btn sm ghost" onClick={onDraftAgain}>
                Draft again
              </button>
            </div>
          </>
        ) : drafting(view.runs.emails) ? (
          <div className="rd-busy" style={{ marginTop: 22 }}>
            <span className="spin" />
            <span>The agent is writing the emails from the plan</span>
          </div>
        ) : (
          <p className="rd-p" style={{ marginTop: 22 }}>
            The agent has not started on the emails yet.
          </p>
        )
      ) : (
        <>
          <div className="wk-sub" style={{ marginTop: 22 }}>
            The {WORDS[emails.length] ?? emails.length} emails, written as the sender
          </div>
          {editing ? (
            <div className="rd-form">
              {drafts.map((d, k) => (
                <div className="rd-mf" key={d.step}>
                  <div className="rd-mail-h">
                    <span className="chip">Email {d.step}</span>
                    <span className="rd-when">{d.when}</span>
                  </div>
                  <label className="fl" htmlFor={`rdms-${d.step}`}>
                    Subject
                  </label>
                  <input className="field" id={`rdms-${d.step}`} value={d.subject} onChange={(e) => setDrafts((xs) => xs.map((x, i) => (i === k ? { ...x, subject: e.target.value } : x)))} />
                  <label className="fl" htmlFor={`rdmb-${d.step}`}>
                    Email
                  </label>
                  <textarea
                    className="field"
                    id={`rdmb-${d.step}`}
                    rows={7}
                    autoFocus={k === 0}
                    value={d.body}
                    onChange={(e) => setDrafts((xs) => xs.map((x, i) => (i === k ? { ...x, body: e.target.value } : x)))}
                  />
                </div>
              ))}
              <div className="m-act">
                <button type="button" className="btn sm" disabled={busy} onClick={() => void saveEdit()}>
                  Save
                </button>
                <button type="button" className="btn sm ghost" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="rd-mails">
              {emails.map((m) => (
                <div className="rd-mail" key={m.step}>
                  <div className="rd-mail-h">
                    <span className="chip">Email {m.step}</span>
                    {m.launched && (
                      <span className="chip ok">
                        <Icon name="check" />
                        Sent to Smartlead
                      </span>
                    )}
                    <span className="rd-when">{m.when}</span>
                  </div>
                  <b className="rd-subj">{m.subject}</b>
                  <div className="rd-mail-b">
                    {m.body.split("\n").map((l, i) => (
                      <Line key={i} text={l} />
                    ))}
                  </div>
                  {footer}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <p className="rd-note">
        <Icon name="info" />
        <span>The first line is written for each person from their own page.</span>
      </p>
      <label className="fl" htmlFor="rd-addr">
        Business postal address, for the footer
      </label>
      <input
        className="field"
        id="rd-addr"
        value={addr}
        placeholder="Street, city, state and ZIP"
        autoComplete="off"
        disabled={approved}
        onChange={(e) => setAddr(e.target.value)}
        onBlur={() => void saveAddr()}
      />
      <div className="rd-acts" id="rd-mail-acts">
        {approved ? (
          <>
            <span className="chip ok">
              <Icon name="check" />
              {sending ? "Sent to Smartlead. It sends each email on its date." : `Approved. The first emails go out ${startDay ?? "on their date"}.`}
            </span>
            {!sending && (
              <button type="button" className="wk-undo" disabled={busy} onClick={() => void run(() => readyApi.unapproveEmails(goalId), null)}>
                Undo approval
              </button>
            )}
          </>
        ) : (
          <>
            {!editing && emails.length > 0 && (
              <button type="button" className="chip link" onClick={openEdit}>
                <Icon name="pen" />
                Edit the emails
              </button>
            )}
            <button type="button" className="btn sm rd-mok" disabled={!can || busy} style={can ? undefined : { opacity: 0.45 }} onClick={() => void approve()}>
              <Icon name="check" />
              Approve the first emails
            </button>
            {!can && !editing && (
              <span className="fine rd-why">{!ready ? "Once the list is ready." : emails.length === 0 ? "Once the emails are written." : "Add the postal address first."}</span>
            )}
          </>
        )}
      </div>
    </section>
  );
}
